import { CloudOff, Wifi, WifiOff } from "lucide-react";
import type { DashboardData } from "../types";
import type { PendingSyncItem } from "../lib/offline";
import { dateLabel } from "../lib/finance";

// The notices above a screen: a failed refresh, a tab running an older build than the server, and
// any warnings the server attached to the data.

export default function StatusBanners({
  data,
  error,
  staleBuild,
  online,
  offlineSince,
  pendingSync,
  syncing,
  syncError,
  refreshing = false,
  onSync,
  onRetry,
  onReviewPending,
}: {
  data: DashboardData | null;
  error: string;
  staleBuild: boolean;
  online: boolean;
  offlineSince: string | null;
  pendingSync: PendingSyncItem[];
  syncing: boolean;
  syncError: string;
  refreshing?: boolean;
  onSync: () => void;
  onRetry: () => void;
  onReviewPending?: (key: string) => void;
}) {
  const offlineLabel = offlineSince ? dateLabel(offlineSince) : "";
  const reviewEntry = pendingSync.find(item => item.needsReview);
  return (
    <>
      {(!online || offlineSince) && data && !error && (
        <div className="error-banner offline-banner" role="status">
          {online ? <Wifi size={20} /> : <WifiOff size={20} />}
          <span>
            <strong>
              {online ? "Showing saved offline data." : "Offline mode."}
            </strong>
            <small>
              {online
                ? (offlineLabel
                    ? `Last synced ${offlineLabel}. New entries sync automatically once server reconnects.`
                    : "New entries sync automatically once server reconnects.")
                : (offlineLabel
                    ? `Last synced ${offlineLabel}. New entries sync automatically when you reconnect.`
                    : "New entries sync automatically when you reconnect.")}
            </small>
          </span>
          <button onClick={onRetry} disabled={!online || refreshing}>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      )}
      {pendingSync.length > 0 && data && (
        <div className="error-banner offline-banner pending-sync" role="status">
          {online ? <Wifi size={20} /> : <WifiOff size={20} />}
          <span>
            <strong>{syncing ? "Syncing" : pendingSync.length} {syncing ? `${pendingSync.length} ` : ""}change{pendingSync.length === 1 ? "" : "s"}{syncing ? "…" : " waiting to sync."}</strong>
            <small>
              {reviewEntry ? "An entry needs correcting before it can sync."
                : online
                ? "Saved entries sync automatically. You can keep adding transactions."
                : "Saved on this device. All entries will sync automatically when you’re back online."}
            </small>
            {syncError && <small role="alert">{syncError}</small>}
            {reviewEntry && onReviewPending && <button onClick={() => onReviewPending(reviewEntry.key)}>Review entry</button>}
            <button onClick={onSync} disabled={!online || syncing}>
              {syncing ? "Syncing…" : "Sync now"}
            </button>
          </span>
        </div>
      )}
      {error && (
        <div className="error-banner" role="alert">
          {online ? <CloudOff size={20} /> : <WifiOff size={20} />}
          <span>
            <strong>
              {data
                ? (online ? "Couldn’t refresh from server." : "Offline mode.")
                : (online ? "Couldn’t connect to Barnyard." : "You’re offline.")}
            </strong>
            <small>
              {error}
              {data && offlineLabel ? ` Showing saved data from ${offlineLabel}.` : ""}
            </small>
          </span>
          <button onClick={onRetry} disabled={!online || refreshing}>
            {refreshing ? "Refreshing…" : "Try again"}
          </button>
        </div>
      )}
      {staleBuild && (
        <div className="error-banner stale-build" role="status">
          <span>
            <strong>This tab is running an older version.</strong>
            <small>Reload to pick up the latest changes.</small>
          </span>
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      )}
      {(data?.warnings || []).map((w) => (
        <div className="error-banner" role="status" key={w}>
          {w}
        </div>
      ))}
    </>
  );
}
